import {
  CheckOutlined,
  DownOutlined,
  ExclamationCircleOutlined,
  RightOutlined,
  UndoOutlined,
} from '@ant-design/icons';
import { Button, Input, Tag, Tooltip } from 'antd';
import React, { useState } from 'react';
import { FormattedMessage as F } from 'umi';

import InputWithPartnerTranslation from '../InputWithPartnerTranslation';
import { bindingExpression } from './translationBinding';

import styles from './index.less';

export interface TestDataRow {
  key: string;
  value: string;
  /** Friendly display label; falls back to the raw key. */
  label?: string;
  /** Muted context shown next to the label ("Theme · Colors"). */
  context?: string;
  /** Shown when no value is set (e.g. the journey-theme sample). */
  placeholder?: string;
  /**
   * The template references this key but the partner has no translation for
   * the previewed language — the row is highlighted so gaps are visible.
   */
  missing?: boolean;
  /** Row has an unsaved edit that the section's onSave can persist. */
  saveable?: boolean;
  /** Persistence for this row is in flight. */
  saving?: boolean;
  /**
   * The record whose data filled this row — a real lead or appointment picked
   * in the preview. Falls back to the panel's `appliedSourceLabel`.
   */
  sourceLabel?: string;
  /**
   * What that record held for this path. Kept beside `value` rather than
   * replacing it, so an edited field can still be put back: the two differing
   * is exactly what "edited" means, and `sourceValue` is the way back.
   * Absent (or empty) means the record had nothing for this path.
   */
  sourceValue?: string;
}

export interface TestDataPanelSection {
  title: React.ReactNode;
  /** Muted explainer under the section title while expanded. */
  hint?: React.ReactNode;
  rows: TestDataRow[];
  onChange: (key: string, value: string) => void;
  /** When set, saveable rows offer a save button that persists the value. */
  onSave?: (key: string, value: string) => void;
  /**
   * Put a row back to what the picked record held. Without it an edited field
   * is a one-way door: the record's value is gone the moment you type over it.
   */
  onRestore?: (key: string, value: string) => void;
  /** Section starts collapsed (e.g. translations, which are preview-only). */
  defaultCollapsed?: boolean;
  /**
   * The rows are **translation keys**, not preview samples.
   *
   * They render as the translation field the rest of the Email Center uses, so a
   * key's wording is edited the same way everywhere — with the translation icon,
   * the key it writes to, and the all-languages popover. A plain text box here
   * looked like a per-preview override, which is the opposite of what saving does:
   * it changes the wording for **every** email using that key.
   */
  translationKeys?: boolean;
  /** Which language the translation field shows and edits. */
  language?: string;
}

interface TestDataPanelProps {
  sections: TestDataPanelSection[];
  /**
   * The lead or appointment the current values came from. Drives the strip
   * above the fields that says how far that record actually reached.
   */
  appliedSourceLabel?: string;
  className?: string;
}

/**
 * How far a picked record reached: how many of the fillable fields it holds a
 * value for, out of how many the email uses.
 *
 * Translation rows are excluded — they are wording from the partner catalog,
 * not record data, and counting them would understate every record.
 */
export function sampleFillCounts(sections: TestDataPanelSection[]): {
  filled: number;
  total: number;
} {
  const rows = sections
    .filter((section) => !section.translationKeys && section.rows.length > 0)
    .flatMap((section) => section.rows);

  return {
    filled: rows.filter((row) => (row.sourceValue ?? '') !== '').length,
    total: rows.length,
  };
}

/**
 * Sample-data side panel of the email previews: one input per placeholder or
 * translation key the template actually uses, labelled with a friendly name
 * (the technical key stays available as a tooltip). Sections are collapsible
 * (a section can start collapsed via defaultCollapsed); sections with no rows
 * are skipped and the panel hides entirely when nothing is fillable.
 * Translation rows highlight missing partner translations and, when the
 * section provides onSave, persist edits to the partner's translation
 * catalog.
 *
 * When the values were filled from a real lead or appointment, the panel says
 * so — how many fields that record reached, which ones it filled, and the way
 * back for the ones since edited. Without it, picking a record whose data does
 * not cover this email (a lead with no appointment against an appointment
 * template) looks exactly like a broken button: the request succeeds and the
 * fields stay empty.
 */
const TestDataPanel: React.FC<TestDataPanelProps> = ({
  sections,
  appliedSourceLabel,
  className,
}) => {
  const [collapsed, setCollapsed] = useState<Record<number, boolean>>({});
  const visible = sections.filter((section) => section.rows.length > 0);

  const { filled: filledCount, total: fillableCount } =
    sampleFillCounts(visible);

  if (visible.length === 0) return null;

  return (
    <div
      className={
        className
          ? `${styles.testVarsPanel} ${className}`
          : styles.testVarsPanel
      }
    >
      {!!appliedSourceLabel && fillableCount > 0 && (
        <div
          className={
            filledCount === 0
              ? `${styles.testVarsSource} ${styles.testVarsSourceEmpty}`
              : styles.testVarsSource
          }
        >
          {filledCount === 0 ? (
            <>
              <ExclamationCircleOutlined />
              <span>
                <F
                  id="pages.emailCenter.editor.sampleFilledNone"
                  defaultMessage="{source} has no data for the values this email uses — nothing was filled in."
                  values={{ source: <strong>{appliedSourceLabel}</strong> }}
                />
              </span>
            </>
          ) : (
            <span>
              <F
                id="pages.emailCenter.editor.sampleFilledFrom"
                defaultMessage="{filled} of {total} fields filled from {source}"
                values={{
                  filled: filledCount,
                  total: fillableCount,
                  source: <strong>{appliedSourceLabel}</strong>,
                }}
              />
            </span>
          )}
        </div>
      )}
      {visible.map((section, index) => {
        const isCollapsed =
          collapsed[index] ?? section.defaultCollapsed ?? false;
        return (
          <React.Fragment key={index}>
            <div
              className={styles.testVarsTitle}
              role="button"
              tabIndex={0}
              onClick={() =>
                setCollapsed((current) => ({
                  ...current,
                  [index]: !isCollapsed,
                }))
              }
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  setCollapsed((current) => ({
                    ...current,
                    [index]: !isCollapsed,
                  }));
                }
              }}
            >
              {isCollapsed ? <RightOutlined /> : <DownOutlined />}
              {section.title}
              <span className={styles.testVarsCount}>
                {section.rows.length}
              </span>
            </div>
            {!isCollapsed && section.hint && (
              <div className={styles.testVarsHint}>{section.hint}</div>
            )}
            {!isCollapsed &&
              section.rows.map((row) => {
                // Only a record that actually held something for this path can
                // mark the row; an empty `sourceValue` means it had nothing.
                const fromRecord =
                  !section.translationKeys && (row.sourceValue ?? '') !== '';
                const edited = fromRecord && row.value !== row.sourceValue;
                const recordLabel = row.sourceLabel ?? appliedSourceLabel;
                return (
                  <div key={row.key} className={styles.testVarRow}>
                    <span className={styles.testVarLabel}>
                      <Tooltip
                        title={<code>{row.key}</code>}
                        mouseEnterDelay={0.4}
                      >
                        <span className={styles.testVarName}>
                          {row.label ?? row.key}
                        </span>
                      </Tooltip>
                      {row.context && (
                        <span className={styles.testVarContext}>
                          {row.context}
                        </span>
                      )}
                      {/* Not for translation rows: the field says it itself, in the
                        same strip every translated field uses. */}
                      {row.missing && !section.translationKeys && (
                        <Tag
                          color="warning"
                          className={styles.testVarMissingTag}
                        >
                          <F
                            id="pages.emailCenter.editor.translationMissing"
                            defaultMessage="Missing"
                          />
                        </Tag>
                      )}
                      {fromRecord && !edited && !!recordLabel && (
                        <Tag className={styles.testVarSourceTag}>
                          <F
                            id="pages.emailCenter.editor.sampleFromRecord"
                            defaultMessage="From {source}"
                            values={{ source: recordLabel }}
                          />
                        </Tag>
                      )}
                      {edited && (
                        <Tooltip
                          title={
                            <F
                              id="pages.emailCenter.editor.sampleEditedSince"
                              defaultMessage="{source} had “{value}”"
                              values={{
                                source: recordLabel,
                                value: row.sourceValue,
                              }}
                            />
                          }
                        >
                          <Tag
                            color="processing"
                            className={styles.testVarSourceTag}
                          >
                            <F
                              id="pages.emailCenter.editor.sampleEdited"
                              defaultMessage="Edited"
                            />
                          </Tag>
                        </Tooltip>
                      )}
                    </span>
                    <div className={styles.testVarInputRow}>
                      {section.translationKeys ? (
                        <InputWithPartnerTranslation
                          value={bindingExpression(row.key)}
                          language={section.language ?? 'de'}
                        />
                      ) : (
                        <Input
                          size="small"
                          status={
                            row.missing && !row.value ? 'warning' : undefined
                          }
                          value={row.value}
                          placeholder={row.placeholder ?? '—'}
                          onChange={(event) =>
                            section.onChange(row.key, event.target.value)
                          }
                        />
                      )}
                      {!section.translationKeys &&
                        section.onSave &&
                        row.saveable && (
                          <Tooltip
                            title={
                              <F
                                id="pages.emailCenter.editor.saveTranslation"
                                defaultMessage="Save this text for all emails"
                              />
                            }
                          >
                            <Button
                              size="small"
                              type="primary"
                              ghost
                              icon={<CheckOutlined />}
                              loading={row.saving}
                              onClick={() =>
                                section.onSave?.(row.key, row.value)
                              }
                            />
                          </Tooltip>
                        )}
                      {edited && section.onRestore && (
                        <Tooltip
                          title={
                            <F
                              id="pages.emailCenter.editor.sampleRestore"
                              defaultMessage="Put {source}’s value back"
                              values={{ source: recordLabel }}
                            />
                          }
                        >
                          <Button
                            size="small"
                            type="text"
                            icon={<UndoOutlined />}
                            onClick={() =>
                              section.onRestore?.(
                                row.key,
                                row.sourceValue ?? '',
                              )
                            }
                          />
                        </Tooltip>
                      )}
                    </div>
                  </div>
                );
              })}
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default React.memo(TestDataPanel);
